/**
 * Copyright (c) 2026 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * Dynamo TBL schema. Field documentation follows the official Dynamo column
 * specification.
 *
 * @author Ludovic Autin <autin@scripps.edu>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */

import { Column, Table } from '../../../mol-data/db';

import Schema = Column.Schema;

const int = Schema.int;
const float = Schema.float;

// from https://www.dynamo-em.org/w/index.php?title=Table

export const DynamoTblSchema = {
    /** tag of particle file in data folder */
    tag: int,
    /** value 1: marks the particle for alignment */
    aligned: int,
    /** value 1: the particle was included in the average */
    averaged: int,
    /** x shift from center (in pixels) */
    dx: float,
    /** y shift from center (in pixels) */
    dy: float,
    /** z shift from center (in pixels) */
    dz: float,
    /** euler angle (rotation around z, in degrees) */
    tdrot: float,
    /** euler angle (rotation around new x, in degrees) */
    tilt: float,
    /** euler angle (rotation around new z, in degrees) */
    narot: float,
    /** cross correlation coefficient */
    cc: float,
    /** cross correlation coefficient after thresholding II */
    cc2: float,
    /** processor that aligned the particle */
    cpu: int,
    /** 0: full range; 1: tilt around y (see dhelp/dtutorial for more options) */
    ftype: int,
    /** minimum angle in the tilt series around tilt axis (e.g. -60) */
    ymintilt: float,
    /** maximum angle in the tilt series around tilt axis (e.g. 60) */
    ymaxtilt: float,
    /** minimum angle in the tilt series around second tilt axis (e.g. -60) */
    xmintilt: float,
    /** maximum angle in the tilt series around second x (e.g. 60) */
    xmaxtilt: float,
    /** free parameter for fourier sampling #1 */
    fs1: float,
    /** free parameter for fourier sampling #2 */
    fs2: float,
    /** tomogram number */
    tomo: int,
    /** arbitrary assignment of regions inside tomograms */
    reg: int,
    /** class number */
    class: int,
    /** arbitrary label */
    annotation: int,
    /** x coordinate in original volume */
    x: float,
    /** y coordinate in original volume */
    y: float,
    /** z coordinate in original volume */
    z: float,
    /** norm of shift vector */
    dshift: float,
    /** difference in axis orientation */
    daxis: float,
    /** difference in narot */
    dnarot: float,
    /** difference in CC */
    dcc: float,
    /** original tag (subboxing) */
    otag: int,
    /** number of added particles (compactions) / subunit label (subboxing) */
    npar: int,
    /** reference; used in multireference projects */
    ref: int,
    /** subreference (e.g. generated by Dynamo PCA) */
    sref: int,
    /** angstrom per pixel */
    apix: float,
    /** defocus (in micron) */
    def: float,
    /** "eigencoefficient" #1 */
    eig1: float,
    /** "eigencoefficient" #2 */
    eig2: float,
};

export type DynamoTblSchema = typeof DynamoTblSchema;
export interface DynamoTblTable extends Table<DynamoTblSchema> {};
